import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal } from '../modal/Modal';
import { EmailInput } from '../input/Inputs';
import { GradientButton } from '../button/Button';
import { resetPassword } from '@/services/auth/emailAuthService';
import { useSnackbar } from '../common/Snackbar';

interface PasswordResetRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const PasswordResetRequestModal: React.FC<PasswordResetRequestModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const result = await resetPassword(email);

      if (result._meta.status === 200) {
        // 성공 시 스낵바 표시하고 모달 닫기
        showSnackbar(result.detail, {
          showCheckIcon: true,
          duration: 3000,
        });
        onClose();
        // 부모에게 성공 알림
        onSuccess?.();
      } else {
        setError('Failed to send reset email');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.response?.status === 404) {
        setError('해당 이메일로 등록된 계정이 없습니다.');
      } else {
        setError('이메일 발송 중 오류가 발생했습니다.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Reset Password" size="medium">
      <ModalContent>
        <Description>We'll send a verification code to this email.</Description>

        <Form onSubmit={handleSubmit}>
          <EmailInput
            label="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            error={error}
            placeholder="your@email.com"
            name="reset-email"
            disabled={isLoading}
          />

          <ButtonContainer>
            <GradientButton type="submit" loading={isLoading} disabled={!email || isLoading} width="100%" height="47px">
              Send Reset Link
            </GradientButton>

            <CancelButton type="button" onClick={handleClose}>
              Back
            </CancelButton>
          </ButtonContainer>
        </Form>
      </ModalContent>
    </Modal>
  );
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.fontGray[600]};
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 32px;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CancelButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.fontGray[400]};
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  padding: 12px;

  &:hover {
    text-decoration: underline;
  }
`;
